/**
 * The intervention status graph and who may walk each edge — pure data, shared so the
 * web's action matrix can be tested against it (#2301). The API enforces it through
 * `apps/api/src/domains/interventions/intervention-state-machine.ts`, which re-exports
 * these under their historical names (`VALID_TRANSITIONS`, `canTransition`,
 * `hasPermission`) and adds the throwing validators; the web's
 * `intervention-action-contract.test.ts` (#2380) is that test.
 */
import { InterventionStatus, UserRole } from './enums';
const SUPER_ADMIN_ONLY = [UserRole.SUPER_ADMIN];
const CLIENT_ROLES = [
    UserRole.SUPER_ADMIN,
    UserRole.ADMIN,
    UserRole.FACILITY_MANAGER,
    UserRole.VIEWER,
];
const TECHNICIAN_ROLES = [UserRole.SUPER_ADMIN, UserRole.TECHNICIAN];
// #2180: only `resolveResumeTarget` picks these; a caller naming one is refused.
export const RESTORED_AFTER_HOLD = [
    InterventionStatus.AWAITING_QUOTE,
    InterventionStatus.AWAITING_ASSIGNMENT,
    InterventionStatus.SCHEDULED,
    InterventionStatus.NEXT_VISIT_SCHEDULED,
];
/** Every status `resolveResumeTarget` may land an ON_HOLD intervention on. */
export const RESUMABLE_FROM_HOLD = [
    ...RESTORED_AFTER_HOLD,
    InterventionStatus.IN_PROGRESS,
];
/**
 * Where `PATCH :id/scheduled-date` (and a `scheduledDate` on `PATCH :id`) may move the
 * planned date. Why each status is in or out is argued where it is enforced, in
 * `InterventionsService` (`apps/api/src/domains/interventions/services/interventions.service.ts`).
 */
export const SCHEDULED_DATE_EDITABLE_STATUSES = new Set([
    InterventionStatus.SCHEDULED,
    InterventionStatus.DELAYED,
    InterventionStatus.IN_PROGRESS,
    InterventionStatus.NEXT_VISIT_SCHEDULED,
    InterventionStatus.AWAITING_VALIDATION,
    InterventionStatus.AWAITING_REVISION,
]);
/**
 * The status graph, with role gates in `ROLE_TRANSITION_PERMISSIONS` below.
 *
 * SYNC WARNING: duplicated in the DB trigger `validate_intervention_status_transition()`
 * (the latest migration redefining it). Any change here MUST ship a new migration
 * updating the trigger; `apps/api/tests/interventions/intervention-status-trigger-parity.spec.ts`
 * fails CI when the two diverge.
 */
export const INTERVENTION_TRANSITIONS = {
    [InterventionStatus.DRAFT]: [
        InterventionStatus.AWAITING_QUOTE,
        InterventionStatus.AWAITING_ASSIGNMENT,
        InterventionStatus.CANCELLED,
    ],
    // Quote-validation sub-graph (#1188). Sharing a curated PENDING quote moves
    // the intervention into AWAITING_VALIDATION; the client's decision then
    // routes it to SCHEDULED (validate), AWAITING_REVISION, or
    // QUOTE_DECLINED. All quote-driven edges are auto/system (see permissions).
    [InterventionStatus.AWAITING_QUOTE]: [
        InterventionStatus.AWAITING_ASSIGNMENT,
        InterventionStatus.AWAITING_VALIDATION,
        InterventionStatus.ON_HOLD,
        InterventionStatus.CANCELLED,
    ],
    // The quote-validation statuses are deliberately NOT holdable (no → ON_HOLD).
    // ON_HOLD → * is a manual CLIENT_ROLES edge, so exposing these as ON_HOLD
    // resume targets would let a user fabricate a quote-validation state without
    // a quote event / shared quote / feedback (#1188). They are reached ONLY via
    // quote events; CANCELLED stays available as the escape.
    [InterventionStatus.AWAITING_VALIDATION]: [
        InterventionStatus.AWAITING_QUOTE,
        InterventionStatus.SCHEDULED,
        // The validated quote assigns the technician even when the client never
        // asked for a date (#1688) — the passage is then agreed on the phone.
        InterventionStatus.AWAITING_SCHEDULING,
        InterventionStatus.AWAITING_REVISION,
        InterventionStatus.QUOTE_DECLINED,
        InterventionStatus.CANCELLED,
    ],
    [InterventionStatus.AWAITING_REVISION]: [
        InterventionStatus.AWAITING_VALIDATION,
        InterventionStatus.CANCELLED,
    ],
    [InterventionStatus.QUOTE_DECLINED]: [
        InterventionStatus.AWAITING_VALIDATION,
        InterventionStatus.AWAITING_QUOTE,
        InterventionStatus.CANCELLED,
    ],
    [InterventionStatus.AWAITING_ASSIGNMENT]: [
        InterventionStatus.SCHEDULED,
        // Assignment of a date-less intervention (#1688): the technician is on the
        // job but the passage is still « à convenir avec le contact sur place ».
        InterventionStatus.AWAITING_SCHEDULING,
        InterventionStatus.ON_HOLD,
        InterventionStatus.CANCELLED,
    ],
    // #1688. Deliberately NOT holdable and with no edge back to
    // AWAITING_ASSIGNMENT: exactly like SCHEDULED, the operator's escape is
    // `unassignIntervention` (SUPER_ADMIN — exempt from the graph and the DB
    // trigger), and no ON_HOLD resume target (`resolveResumeTarget`) can
    // reconstruct « a technician is assigned but has no date », so holding here
    // would strand the intervention rather than park it.
    [InterventionStatus.AWAITING_SCHEDULING]: [
        InterventionStatus.SCHEDULED,
        InterventionStatus.CANCELLED,
    ],
    [InterventionStatus.SCHEDULED]: [
        InterventionStatus.IN_PROGRESS,
        InterventionStatus.DELAYED,
        InterventionStatus.ON_HOLD,
        InterventionStatus.CANCELLED,
    ],
    [InterventionStatus.DELAYED]: [
        InterventionStatus.IN_PROGRESS,
        InterventionStatus.SCHEDULED,
        InterventionStatus.ON_HOLD,
        InterventionStatus.CANCELLED,
    ],
    [InterventionStatus.IN_PROGRESS]: [
        InterventionStatus.COMPLETED,
        // A signed report with a "suite prévue" return visit moves IN_PROGRESS
        // straight to NEXT_VISIT_SCHEDULED (report-faithful-mirror). Mirrored in the
        // DB trigger migration 0143 and kept in parity by the parity spec.
        InterventionStatus.NEXT_VISIT_SCHEDULED,
        InterventionStatus.ON_HOLD,
        InterventionStatus.TO_RESCHEDULE,
        InterventionStatus.CANCELLED,
    ],
    [InterventionStatus.COMPLETED]: [
        InterventionStatus.CLOSED,
        InterventionStatus.NEXT_VISIT_SCHEDULED,
        InterventionStatus.AWAITING_QUOTE,
    ],
    [InterventionStatus.TO_RESCHEDULE]: [
        InterventionStatus.SCHEDULED,
        InterventionStatus.CANCELLED,
    ],
    [InterventionStatus.NEXT_VISIT_SCHEDULED]: [
        InterventionStatus.IN_PROGRESS,
        InterventionStatus.DELAYED,
        InterventionStatus.ON_HOLD,
        InterventionStatus.CANCELLED,
    ],
    // #1716. CLOSED is otherwise terminal; the ONE exit is the mirror of the
    // auto-close, taken when the mark-paid that closed it is undone
    // (INVOICE_EVENTS.UNPAID). Machine-only, like COMPLETED -> CLOSED itself:
    // nothing manual may reopen a closed intervention.
    [InterventionStatus.CLOSED]: [InterventionStatus.COMPLETED],
    [InterventionStatus.ON_HOLD]: [
        ...RESTORED_AFTER_HOLD,
        InterventionStatus.IN_PROGRESS,
        InterventionStatus.TO_RESCHEDULE,
        InterventionStatus.CANCELLED,
    ],
    [InterventionStatus.CANCELLED]: [InterventionStatus.AWAITING_ASSIGNMENT],
};
// Helper function to create transition keys
const t = (from, to) => `${from}->${to}`;
const ROLE_TRANSITION_PERMISSIONS = {
    [t(InterventionStatus.DRAFT, InterventionStatus.AWAITING_QUOTE)]: {
        roles: CLIENT_ROLES,
    },
    [t(InterventionStatus.DRAFT, InterventionStatus.AWAITING_ASSIGNMENT)]: {
        roles: CLIENT_ROLES,
    },
    [t(InterventionStatus.AWAITING_ASSIGNMENT, InterventionStatus.SCHEDULED)]: {
        roles: TECHNICIAN_ROLES,
    },
    // #1688 — same acceptance gesture as the edge above, taken on an intervention
    // created without a `requestedDate`.
    [t(InterventionStatus.AWAITING_ASSIGNMENT, InterventionStatus.AWAITING_SCHEDULING)]: {
        roles: TECHNICIAN_ROLES,
    },
    // #1688 — the technician agreed the passage with the on-site contact and
    // wrote it (`POST :id/technician/schedule`).
    [t(InterventionStatus.AWAITING_SCHEDULING, InterventionStatus.SCHEDULED)]: {
        roles: TECHNICIAN_ROLES,
    },
    [t(InterventionStatus.AWAITING_QUOTE, InterventionStatus.AWAITING_ASSIGNMENT)]: {
        roles: CLIENT_ROLES,
    },
    // Quote-validation sub-graph (#1188). Every edge below is driven by a quote
    // event through InterventionLifecycleListener (SUPER_ADMIN + isAutoTransition),
    // never a direct user status change — hence requiresAuto. The lone exception
    // is the manual SA redraft QUOTE_DECLINED -> AWAITING_QUOTE.
    [t(InterventionStatus.AWAITING_QUOTE, InterventionStatus.AWAITING_VALIDATION)]: {
        roles: SUPER_ADMIN_ONLY,
        requiresAuto: true,
    },
    [t(InterventionStatus.AWAITING_VALIDATION, InterventionStatus.AWAITING_QUOTE)]: {
        roles: SUPER_ADMIN_ONLY,
        requiresAuto: true,
    },
    [t(InterventionStatus.AWAITING_VALIDATION, InterventionStatus.SCHEDULED)]: {
        roles: SUPER_ADMIN_ONLY,
        requiresAuto: true,
    },
    // #1688 — the date-less branch of the same quote-validation auto-schedule.
    [t(InterventionStatus.AWAITING_VALIDATION, InterventionStatus.AWAITING_SCHEDULING)]: {
        roles: SUPER_ADMIN_ONLY,
        requiresAuto: true,
    },
    [t(InterventionStatus.AWAITING_VALIDATION, InterventionStatus.AWAITING_REVISION)]: {
        roles: SUPER_ADMIN_ONLY,
        requiresAuto: true,
    },
    [t(InterventionStatus.AWAITING_VALIDATION, InterventionStatus.QUOTE_DECLINED)]: {
        roles: SUPER_ADMIN_ONLY,
        requiresAuto: true,
    },
    [t(InterventionStatus.AWAITING_REVISION, InterventionStatus.AWAITING_VALIDATION)]: {
        roles: SUPER_ADMIN_ONLY,
        requiresAuto: true,
    },
    [t(InterventionStatus.QUOTE_DECLINED, InterventionStatus.AWAITING_VALIDATION)]: {
        roles: SUPER_ADMIN_ONLY,
        requiresAuto: true,
    },
    // Manual SA redraft after a refusal: back to "En attente de devis" to start over.
    [t(InterventionStatus.QUOTE_DECLINED, InterventionStatus.AWAITING_QUOTE)]: {
        roles: SUPER_ADMIN_ONLY,
    },
    [t(InterventionStatus.SCHEDULED, InterventionStatus.IN_PROGRESS)]: {
        roles: TECHNICIAN_ROLES,
    },
    [t(InterventionStatus.SCHEDULED, InterventionStatus.DELAYED)]: {
        roles: SUPER_ADMIN_ONLY,
        requiresAuto: true,
    },
    [t(InterventionStatus.DELAYED, InterventionStatus.IN_PROGRESS)]: {
        roles: TECHNICIAN_ROLES,
    },
    [t(InterventionStatus.DELAYED, InterventionStatus.SCHEDULED)]: {
        roles: SUPER_ADMIN_ONLY,
    },
    [t(InterventionStatus.IN_PROGRESS, InterventionStatus.COMPLETED)]: {
        roles: TECHNICIAN_ROLES,
    },
    [t(InterventionStatus.IN_PROGRESS, InterventionStatus.TO_RESCHEDULE)]: {
        roles: TECHNICIAN_ROLES,
    },
    // Report follow-up "suite prévue" with a scheduled return visit
    // (report-faithful-mirror). `validateTransitionRequirements` already requires
    // `scheduledDate` for a NEXT_VISIT_SCHEDULED target.
    [t(InterventionStatus.IN_PROGRESS, InterventionStatus.NEXT_VISIT_SCHEDULED)]: {
        roles: TECHNICIAN_ROLES,
    },
    [t(InterventionStatus.COMPLETED, InterventionStatus.CLOSED)]: {
        roles: SUPER_ADMIN_ONLY,
        requiresAuto: true,
    },
    // #1716 — driven ONLY by the UNPAID listener (isAutoTransition), never by a
    // manual status change, exactly like the COMPLETED -> CLOSED edge it undoes.
    [t(InterventionStatus.CLOSED, InterventionStatus.COMPLETED)]: {
        roles: SUPER_ADMIN_ONLY,
        requiresAuto: true,
    },
    [t(InterventionStatus.COMPLETED, InterventionStatus.NEXT_VISIT_SCHEDULED)]: {
        roles: SUPER_ADMIN_ONLY,
    },
    [t(InterventionStatus.COMPLETED, InterventionStatus.AWAITING_QUOTE)]: {
        roles: TECHNICIAN_ROLES,
    },
    [t(InterventionStatus.TO_RESCHEDULE, InterventionStatus.SCHEDULED)]: {
        roles: SUPER_ADMIN_ONLY,
    },
    [t(InterventionStatus.NEXT_VISIT_SCHEDULED, InterventionStatus.IN_PROGRESS)]: {
        roles: TECHNICIAN_ROLES,
    },
    [t(InterventionStatus.NEXT_VISIT_SCHEDULED, InterventionStatus.DELAYED)]: {
        roles: SUPER_ADMIN_ONLY,
        requiresAuto: true,
    },
    [t('*', InterventionStatus.ON_HOLD)]: {
        roles: CLIENT_ROLES,
    },
    ...Object.fromEntries(RESTORED_AFTER_HOLD.map(to => [
        t(InterventionStatus.ON_HOLD, to),
        { roles: CLIENT_ROLES, requiresAuto: true },
    ])),
    [t(InterventionStatus.ON_HOLD, '*')]: {
        roles: CLIENT_ROLES,
    },
    [t('*', InterventionStatus.CANCELLED)]: {
        roles: CLIENT_ROLES,
    },
    [t(InterventionStatus.CANCELLED, InterventionStatus.AWAITING_ASSIGNMENT)]: {
        roles: CLIENT_ROLES,
    },
};
/** Is `from -> to` an edge of the graph at all (role aside)? */
export function canTransitionIntervention(from, to) {
    return INTERVENTION_TRANSITIONS[from]?.includes(to) ?? false;
}
/** May `role` walk `from -> to`? Does not check the edge exists — see above. */
export function hasInterventionTransitionPermission(from, to, role, isAutoTransition = false) {
    const transitionKey = `${from}->${to}`;
    const wildcardToKey = `*->${to}`;
    const wildcardFromKey = `${from}->*`;
    const permission = ROLE_TRANSITION_PERMISSIONS[transitionKey] ||
        ROLE_TRANSITION_PERMISSIONS[wildcardToKey] ||
        ROLE_TRANSITION_PERMISSIONS[wildcardFromKey];
    // `requiresAuto` edges are machine-only (listener/cron, isAutoTransition=true)
    // — enforced even for SUPER_ADMIN, BEFORE the role bypass below, so a manual
    // status change (e.g. the generic PATCH :id) can never fabricate the
    // quote-validation states (AWAITING_VALIDATION / AWAITING_REVISION /
    // QUOTE_DECLINED) out of band, without the quote event, persisted feedback,
    // or curation guard. `forceStatus()` bypasses the state machine entirely for
    // the genuine SUPER_ADMIN escape hatch.
    if (permission?.requiresAuto && !isAutoTransition) {
        return false;
    }
    if (role === UserRole.SUPER_ADMIN) {
        return true;
    }
    if (!permission) {
        return false;
    }
    return permission.roles.includes(role);
}
